import { useEffect, useState } from 'react';
import List from '../common/List';
import GroupModal from './GroupModal';
import { Row, Col, Button as FilledButton } from 'reactstrap';
import UserModal, { UserModalType } from '../user/UserModal';
import SkillModal from './SkillModal';
import { useHistory, useLocation } from 'react-router-dom';
import { useSetMobilePageTitle } from 'contexts/MobilePageTitleContext';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { Button as ConfirmButton } from 'reactstrap';


import { ReactComponent as MSLogo } from 'assets/icons/ms-logo.svg';
import {
  useGetGroupsQuery,
  useGetLevelOfSkillsQuery,
  useUserSearchQuery,
  useUpdateAzureTenantMutation,
  useRemoveAzureTenantMutation,
  useGetCompanyQuery,
  useGetProjectStagesQuery,
  useGetCurrentUserQuery,
  useUpdateCompanyMutation,
} from 'utils/wemble-api';
import { LevelOfSkillDto, ProjectCategoryDto, ProjectStageDto, useGetProjectCategoriesQuery, UserDto } from 'utils/wemble-api.generated';
import InviteADUsersModal from 'components/user/InviteADUsersModal';
import ProjectStageModal from './ProjectStageModal';
import organization from 'utils/organization';
import { renderCustomHtml } from 'utils/customHtml';
import ProjectCategoryModal from './ProjectCategoryModal';
import MicrosoftEntraSetup, { isOwnEntraState, newEntraState } from './MicrosoftEntraSetup';
import { makeStyles } from '@material-ui/core';
import Modal from '../shared/Modal';

const useStyles = makeStyles(() => ({

  code__input: {
    backgroundColor: 'white',
    fontFamily: 'monospace',
    borderRadius: '.25rem',
    padding: '.75rem 1.25rem',
    width: '100%',
    transition: '0.1s',
    height: '300px',
    border: '1px solid rgba(0,0,0,.125)',
    position: 'relative',
    resize: 'none',
    '&:focus': {
      outline: 'none',
    },
    '&::-webkit-scrollbar': {
      width: 2,
    },
    display: '-webkit-box',
    boxOrient: 'vertical',
    textOverflow: 'ellipsis',
    fontSize: 14,

  },
  code__preview: {
    backgroundColor: 'white',
    borderRadius: '.25rem',
    padding: '.75rem',
    width: '100%',

  }
}))



const GroupsPage = () => {
  const classes = useStyles();
  useSetMobilePageTitle('Admin');

  const history = useHistory();
  const { search, pathname } = useLocation();

  const [selectedUser, setSelectedUser] = useState<UserDto | undefined>();
  const [userModalType, setUserModalType] = useState<UserModalType | undefined>();

  const [selectedSkill, setSelectedSkill] = useState<LevelOfSkillDto | undefined>();
  const [selectedProjectStage, setSelectedProjectStage] = useState<ProjectStageDto | undefined>();
  const [selectedProjectCategory, setSelectedProjectCategory] = useState<ProjectCategoryDto | undefined>();

  const [showSkillModal, setShowSkillModal] = useState(false);
  const [showProjectStageModal, setShowProjectStageModal] = useState(false);
  const [showProjectCategoryModal, setShowProjectCategoryModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showAzureUserModal, setShowAzureUserModal] = useState(false);
  const [showRemoveAzureConfirmation, setShowRemoveAzureConfirmation] = useState(false);


  const { data: levelOfSkills } = useGetLevelOfSkillsQuery();
  const { data: projectStages } = useGetProjectStagesQuery();
  const { data: projectCategories } = useGetProjectCategoriesQuery();

  const { data: groupsRes } = useGetGroupsQuery();
  const { data: searchResult } = useUserSearchQuery({ includeAdmins: true, includeUncompleted: true }, { refetchOnMountOrArgChange: true, refetchOnReconnect: true });
  const users = searchResult?.users;
  const { data: company, refetch: refetchCompany } = useGetCompanyQuery();
  const [updateAzureTenant] = useUpdateAzureTenantMutation();
  const [removeAzureTenant, { isLoading }] = useRemoveAzureTenantMutation();
  const [updateCompany] = useUpdateCompanyMutation();

  const admins = users?.filter((user) => user.companyAdmin);
  const groups = groupsRes?.groups;

  const [customHtml, setCustomHtml] = useState(company?.customHtml);

  const handleOpenUserModal = (type: UserModalType) => (user?: UserDto | undefined) => {
    setSelectedUser(user);
    setUserModalType(type);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(undefined);
    setUserModalType(undefined);
  };

  const handleToggleSkillModal = (open: boolean) => (skill?: LevelOfSkillDto | undefined) => {
    setSelectedSkill(skill);
    setShowSkillModal(open);
  };

  const handleToggleProjectStageModal = (open: boolean) => (stage?: ProjectStageDto | undefined) => {
    setSelectedProjectStage(stage);
    setShowProjectStageModal(open);
  };

  const handleToggleProjectCategoryModal = (open: boolean) => (category?: ProjectCategoryDto | undefined) => {
    setSelectedProjectCategory(category);
    setShowProjectCategoryModal(open);
  };


  const { data: user } = useGetCurrentUserQuery();

  if (!user) return null;

  const organizationConfig = organization(user.company);


  useEffect(() => {
    setCustomHtml(company?.customHtml)
  }, [company])

  // Update user data in modal if users list changes.
  useEffect(() => {
    setSelectedUser((prevSelectedUser) => users?.find((u) => u._id === prevSelectedUser?._id));
  }, [users]);

  useEffect(() => {
    const searchObject = Object.fromEntries(
      search
        .substring(1)
        .split('&')
        .map((v) => v.split('=')),
    );
    console.log('GroupsPage search params:', searchObject);
    if (searchObject['tenant']) {
      if (!isOwnEntraState(searchObject['state'])) {
        history.replace(pathname);
        return;
      }
      console.log('Updating Azure tenant with ID:', searchObject['tenant']);
      updateAzureTenant({
        body: {
          tenantId: searchObject['tenant'],
        },
      }).then(async () => {
        console.log('Azure tenant updated successfully');
        await refetchCompany();
        history.replace(pathname);
      }).catch((error) => {
        console.error('Failed to update Azure tenant:', error);
      });
    }
  }, [search, pathname, history]);

  const handleRemoveAzureADTenant = async () => {
    setShowRemoveAzureConfirmation(true);
  };

  const confirmRemoveAzureADTenant = async () => {
    await removeAzureTenant();
    await refetchCompany();
    setShowRemoveAzureConfirmation(false);
  };

  return (
    <Row>
      <Col
        sm="12"
        md={{
          size: 8,
          offset: 2,
        }}
      >
        <InviteADUsersModal
          open={showAzureUserModal}
          onClose={() => setShowAzureUserModal(false)}
          azureADTenantId={company?.azureADTenantId}
        />
        <UserModal
          isOpen={userModalType !== undefined}
          handleClose={handleCloseUserModal}
          user={selectedUser}
          type={userModalType}
        />

        <GroupModal title="New Group" isOpen={showGroupModal} onClose={() => setShowGroupModal(false)} />



        <SkillModal
          title={selectedSkill?.name ?? "New skill level"}
          isOpen={showSkillModal}
          data={selectedSkill}
          onClose={handleToggleSkillModal(false)}
        />
        <ProjectStageModal
          title={selectedProjectStage?.name ?? "New project stage"}
          isOpen={showProjectStageModal}
          data={selectedProjectStage}
          onClose={handleToggleProjectStageModal(false)}
        />
        <ProjectCategoryModal
          title={selectedProjectCategory?.name ?? "New project category"}
          isOpen={showProjectCategoryModal}
          data={selectedProjectCategory}
          onClose={handleToggleProjectCategoryModal(false)}
        />


        <Grid container justify="space-between" alignItems="center">
          <h2
            style={{
              marginBottom: 24,
              marginTop: 20,
            }}
          >
            {groupsRes?.companyName || <br />}
          </h2>

          <div>
            <Grid container direction="column" alignItems="flex-end" spacing={1}>
              {!organizationConfig.newEntraUI && (
                <>
                  {company?.azureADTenantId && company?.azureADTenantId.length > 0 ? (
                    <>
                      <Button startIcon={<MSLogo />} onClick={() => setShowAzureUserModal(true)} disabled={isLoading}>
                        Add users
                      </Button>
                      <Button
                        startIcon={<MSLogo />}
                        onClick={handleRemoveAzureADTenant}
                        disabled={isLoading}
                      >
                        Remove from my organization
                      </Button>
                    </>
                  ) : process.env.REACT_APP_AAD_APP_CLIENT_ID ? (
                    <Button
                      startIcon={<MSLogo />}
                      onClick={() => {
                        window.location.href = `https://login.microsoftonline.com/common/adminconsent?client_id=${process.env.REACT_APP_AAD_APP_CLIENT_ID}&redirect_uri=${window.location.href}&state=${newEntraState()}`;
                      }}
                    >
                      Add to my organization
                    </Button>
                  ) : null}
                </>
              )}
            </Grid>
          </div>
        </Grid>

        <List
          dataType="user"
          data={admins}
          title={'Administrators'}
          handleItemClick={handleOpenUserModal('company-administrator')}
          handleAddItem={handleOpenUserModal('company-administrator')}
        />

        <Button
          variant="outlined"
          onClick={() => history.push('/users/all')}
          style={{ marginTop: 8, marginBottom: 16 }}
        >
          Manage All Users
        </Button>

        <List
          dataType="group"
          data={groups}
          title={'Groups'}
          handleAddItem={() => setShowGroupModal(true)}
          handleItemClick={(group) => history.push(`/groups/${group._id}`)}
          isGroup
        />
        <List
          dataType="levelOfSkill"
          data={levelOfSkills}
          title={'Skill levels'}
          handleAddItem={handleToggleSkillModal(true)}
          handleItemClick={handleToggleSkillModal(true)}
          isSkillView
          isGroup
        />
        {organizationConfig.enableProjectStages && (<List
          dataType="projectStage"
          data={projectStages}
          title={'Project stages'}
          handleAddItem={handleToggleProjectStageModal(true)}
          handleItemClick={handleToggleProjectStageModal(true)}
          isGroup
        />)}
        {organizationConfig.enableProjectStages && organizationConfig.projectInvoicing && (<List
          dataType="projectCategory"
          data={projectCategories}
          title={'Project categories'}
          handleAddItem={handleToggleProjectCategoryModal(true)}
          handleItemClick={handleToggleProjectCategoryModal(true)}
          isGroup
        />)}

        {
          organization(company?._id).enableCustomHtml && (<>
            <h4>Custom html</h4>
            <p>The following text strings get replaced with actual values: USER_NAME, USER_CUSTOM_ID, USER_WEMBLE_ID, USER_EMAIL, USER_PHOTO_URL</p>
            {customHtml && (<><h5>Live preview</h5><div className={classes.code__preview}
              dangerouslySetInnerHTML={{ __html: renderCustomHtml(customHtml, user) }}
            /></>)}
            <br />
            <h5>HTML Code            <FilledButton style={{ float: 'right' }} color="primary" disabled={customHtml == company?.customHtml} onClick={() => updateCompany({ companyUpdateParams: { name: company?.name || '', customHtml: customHtml } })}>Save</FilledButton>
            </h5>
            <br />
            <textarea
              id="edit_html"
              className={classes.code__input}
              value={customHtml}
              onChange={e => setCustomHtml(e.target.value)} />
            <br />

            <br /><br /><br />

          </>
          )
        }

        {organizationConfig.newEntraUI && (
          <MicrosoftEntraSetup
            company={company}
            onRefetch={refetchCompany}
            onRemoveTenant={handleRemoveAzureADTenant}
          />
        )}

        {/* Add this new modal for Azure AD tenant removal confirmation */}
        <Modal visible={showRemoveAzureConfirmation} close={() => setShowRemoveAzureConfirmation(false)} customWidth={400}>
          <h3>Confirm Removal</h3>
          <p>Are you sure you want to remove the Azure AD tenant from your organization?</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
            <Button color="default" onClick={() => setShowRemoveAzureConfirmation(false)} style={{ marginRight: '10px' }}>
              Cancel
            </Button>
            <ConfirmButton color="danger" onClick={confirmRemoveAzureADTenant}>
              Remove
            </ConfirmButton>
          </div>
        </Modal>

      </Col>
    </Row>
  );
};

export default GroupsPage;
